// src/App.tsx
import React, { useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { videos, isPlanVariantAction } from './videoConfig'
import {
  AVATARS,
  ACCOUNT_TYPES,
  ACTION_TYPES,
  NAVIGATION_TAGS,
  TIMING_CONSTANTS,
  XROAD_PAGES
} from './constants'
import { LanguageCode } from './LanguageSelector'

// Custom hooks
import { useVideoNavigation } from './hooks/useVideoNavigation'
import { useOverlayState } from './hooks/useOverlayState'
import { useVideoPlayer } from './hooks/useVideoPlayer'
import { useAppReducer, ActionType } from './hooks/useAppReducer'

// Components
import VideoPlayer from './components/VideoPlayer'
import ControlBar from './components/ControlBar'
import PageManager from './components/PageManager'
import CallMeForm from './CallMeForm'

const { FADE_DURATION, CONTROL_BAR_HEIGHT } = TIMING_CONSTANTS

export default function App() {
  // i18next hook
  const { i18n } = useTranslation()

  // Use unified state management with reducer
  const { dispatch, selectors } = useAppReducer()
  const {
    selectedLanguage,
    selectedAvatar,
    selectedVariantIntro,
    currentIndex,
    isPaused,
    sourcePath,
    contractType
  } = selectors

  // Initialize source path from URL
  useEffect(() => {
    dispatch({
      type: ActionType.SET_SOURCE_PATH,
      payload: window.location.pathname + window.location.search
    })
  }, [dispatch])

  // Synchronize i18next with selectedLanguage
  useEffect(() => {
    if (selectedLanguage && i18n.language !== selectedLanguage) {
      i18n.changeLanguage(selectedLanguage as string)
    }
  }, [selectedLanguage, i18n])

  // Use custom hooks
  const {
    overlays,
    overlayData,
    setShowLinkOverlay,
    setShowHover,
    setShowPanel,
    setShowAccounts,
    setShowAccountsIntro,
    setShowContactForm,
    setShowQA,
    setShowXroad,
    setShowCallMeForm,
    setLinkUrl,
    setPanelData,
    setShowPanelButton,
    hideAllOverlays,
  } = useOverlayState()

  // Destructure overlay states for easier access
  const {
    showLinkOverlay,
    showHover,
    showPanel,
    showAccounts,
    showAccountsIntro,
    showContactForm,
    showQA,
    showXroad,
    showCallMeForm
  } = overlays

  const { linkUrl, panelData, showPanelButton } = overlayData

  const linkTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Report video change to Google Tag / GTM
  const sendVideoChangeEvent = useCallback((videoTitle: string, videoSrc: string, index: number) => {
    try {
      if ((window as any).gtag) {
        (window as any).gtag('event', 'video_change', {
          video_title: videoTitle,
          video_src: videoSrc,
          index,
        })
      } else if ((window as any).dataLayer && Array.isArray((window as any).dataLayer)) {
        (window as any).dataLayer.push({
          event: 'video_change',
          video_title: videoTitle,
          video_src: videoSrc,
          index,
        })
      }
    } catch (e) {
      console.warn('sendVideoChangeEvent failed', e)
    }
  }, [])

  // Use video player hook for refs
  const { videoRef, sourceRef } = useVideoPlayer({
    currentIndex,
    selectedAvatar,
    selectedLanguage,
    selectedVariantIntro,
    onVideoChange: sendVideoChangeEvent
  })

  // Use navigation hook
  const {
    hasPrev,
    hasNext,
    hasPrevCategory,
    hasNextCategory,
    prevTitle,
    nextTitle,
    prevCategoryTitle,
    nextCategoryTitle
  } = useVideoNavigation({
    currentIndex,
    selectedAvatar,
    selectedLanguage,
    selectedVariantIntro
  })

  // Reset avatar when going back to start
  useEffect(() => {
    if (currentIndex < 1) {
      dispatch({ type: ActionType.RESET_AVATAR })
    }
  }, [currentIndex, dispatch])

  // Handle video actions and overlays when video changes
  useEffect(() => {
    // Reset all overlays atomically
    hideAllOverlays()
    if (linkTimerRef.current) {
      clearTimeout(linkTimerRef.current)
    }

    const action = videos[currentIndex].action

    // Handle link overlay with delay
    if (action?.type === ACTION_TYPES.LINK && action.payload.timeToShow != null) {
      const delayMs = action.payload.timeToShow * 1000
      linkTimerRef.current = setTimeout(() => {
        setLinkUrl(action.payload.url)
        setShowLinkOverlay(true)
      }, delayMs)
    }

    // Handle account-specific link overlays
    if (isPlanVariantAction(action)) {
      const planAction = action  // TypeScript now knows this is PlanVariantAction
      const lang = selectedLanguage === 'sk' ? 'sk' : 'cz'

      if (selectedVariantIntro === ACCOUNT_TYPES.GOLD)
        setLinkUrl(lang === 'cz' ? planAction.payload.url_gold_cz : planAction.payload.url_gold_sk)
      else if (selectedVariantIntro === ACCOUNT_TYPES.RENTIER)
        setLinkUrl(lang === 'cz' ? planAction.payload.url_rentier_cz : planAction.payload.url_rentier_sk)
      else if (selectedVariantIntro === ACCOUNT_TYPES.MAX)
        setLinkUrl(lang === 'cz' ? planAction.payload.url_max_cz : planAction.payload.url_max_sk)

      setShowLinkOverlay(true)
    }

    // Handle panel button with delay
    if (action?.type === ACTION_TYPES.PANEL && action.payload.timeToShow != null) {
      const delayMs = action.payload.timeToShow * 1000
      linkTimerRef.current = setTimeout(() => {
        setShowPanelButton(true)
      }, delayMs)
    }

    // Handle avatar selection hover
    if (videos[currentIndex].action?.type === ACTION_TYPES.SELECT_AVATAR) {
      setShowHover(true)
    }

    // Show special pages based on action type
    showSpecialPage(action?.type)

    return () => {
      if (linkTimerRef.current) {
        clearTimeout(linkTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, selectedVariantIntro])

  // Auto-advance when avatar is selected
  useEffect(() => {
    if (selectedAvatar !== null) {
      dispatch({
        type: ActionType.SET_VIDEO_INDEX,
        payload: currentIndex + 1
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAvatar, dispatch])

  // Show special page based on action type
  const showSpecialPage = (type?: string) => {
    // First hide all full-page overlays
    setShowAccounts(false)
    setShowContactForm(false)
    setShowAccountsIntro(false)
    setShowQA(false)
    setShowXroad(false)

    // Then show the specific one
    switch (type) {
      case ACTION_TYPES.XROAD:
        setShowXroad(true)
        break
      case ACTION_TYPES.SWITCH:
        setShowAccounts(true)
        break
      case ACTION_TYPES.SWITCH_INTRO:
        dispatch({ type: ActionType.SET_VARIANT_INTRO, payload: null })
        setShowAccountsIntro(true)
        break
      case ACTION_TYPES.CONTACT_FORM:
        setShowContactForm(true)
        break
      case ACTION_TYPES.QA:
        setShowQA(true)
        break
    }
  }

  // Navigation functions
  const switchToIndex = (nextIndex: number) => {
    // Validate avatar selection
    if (nextIndex > currentIndex &&
        videos[currentIndex].action?.type === ACTION_TYPES.SELECT_AVATAR &&
        selectedAvatar === null) {
      return
    }

    // Skip examples video for Max account
    if (selectedVariantIntro === ACCOUNT_TYPES.MAX &&
        videos[nextIndex].action?.type === ACTION_TYPES.EXAMPLES) {
      switchToIndex(nextIndex > currentIndex ? nextIndex + 1 : nextIndex - 1)
      return
    }

    try {
      videoRef.current && videoRef.current.pause()
    } catch (e) {
      console.error('Error pausing video:', e)
    }

    setTimeout(() => {
      dispatch({ type: ActionType.SET_VIDEO_INDEX, payload: nextIndex })
      dispatch({ type: ActionType.SET_PAUSE_STATE, payload: false })
      showSpecialPage(videos[nextIndex].action?.type)
    }, FADE_DURATION)
  }

  const handleEnded = () => {
    if (hasNext) {
      switchToIndex(currentIndex + 1)
    }
  }

  const handleLoadedData = () => {
    const v = videoRef.current
    if (!v) return
    // Start playback only if paused - prevents duplicate play in Safari/Edge
    if (v.paused && !isPaused) {
      v.play().catch(() => { /* autoplay blocked - ignore */ })
    }
  }

  const handleAvatarSelect = (avatar: string) => {
    dispatch({ type: ActionType.SET_AVATAR, payload: avatar })
  }

  const handlePanelToggle = () => {
    const action = videos[currentIndex].action
    if (action?.type === ACTION_TYPES.PANEL) {
      setPanelData(action.payload.variant)
      setShowPanel(true)
    }
  }

  const handlePanelClose = () => {
    setShowPanel(false)
  }

  // Page handlers
  const showQAPage = () => {
    showSpecialPage(ACTION_TYPES.QA)
  }

  const closeQAPage = () => {
    showSpecialPage(ACTION_TYPES.CONTACT_FORM)
  }

  const handlePlanSelect = (planKey: string) => {
    dispatch({ type: ActionType.SET_CONTRACT_TYPE, payload: planKey })
    setShowContactForm(true)
    setShowAccounts(false)
    dispatch({ type: ActionType.SET_VIDEO_INDEX, payload: currentIndex + 1 })
  }

  const handlePlanIntroSelect = (planKey: string) => {
    dispatch({ type: ActionType.SET_VARIANT_INTRO, payload: planKey })
    setShowAccountsIntro(false)
    dispatch({ type: ActionType.SET_VIDEO_INDEX, payload: currentIndex + 1 })
  }

  const afterFormSend = () => {
    setShowContactForm(false)
    dispatch({ type: ActionType.SET_VIDEO_INDEX, payload: currentIndex + 1 })
  }

  const onSelectLanguage = (code: LanguageCode) => {
    dispatch({ type: ActionType.SET_LANGUAGE, payload: String(code) })
  }

  const onSelectPage = (page: number) => {
    if (page === XROAD_PAGES.CONTINUE) {
      switchToIndex(currentIndex + 1)
      return
    }
    if (page === XROAD_PAGES.INTRO_ACCOUNTS) {
      dispatch({ type: ActionType.SET_AVATAR, payload: AVATARS.ROBERT })
      const idx = videos.findIndex(v => v.tag === NAVIGATION_TAGS.INTRO_ACCOUNTS)
      if (idx !== -1) {
        switchToIndex(idx)
      }
      return
    }
    // default (XROAD_PAGES.OPEN_ACCOUNT)
    dispatch({ type: ActionType.SET_AVATAR, payload: AVATARS.ROBERT })
    const idx = videos.findIndex(v => v.tag === NAVIGATION_TAGS.OPEN_ACCOUNT)
    if (idx !== -1) {
      switchToIndex(idx)
    }
  }

  // Control bar handlers
  const togglePause = () => {
    const v = videoRef.current
    if (v) {
      if (isPaused) {
        v.play()
      } else {
        v.pause()
      }
      dispatch({ type: ActionType.TOGGLE_PAUSE })
    }
  }

  const goToPrev = () => hasPrev && switchToIndex(currentIndex - 1)
  const goToNext = () => hasNext && switchToIndex(currentIndex + 1)

  const goToPrevCategory = () => {
    if (!hasPrevCategory) return
    let idx = currentIndex - 1
    while (idx >= 0) {
      if (videos[idx].isCategoryVideo) {
        switchToIndex(idx)
        return
      }
      idx--
    }
  }

  const goToNextCategory = () => {
    if (!hasNextCategory) return
    let idx = currentIndex + 1
    while (idx < videos.length) {
      if (videos[idx].isCategoryVideo) {
        switchToIndex(idx)
        return
      }
      idx++
    }
  }

  const handleCallMeClick = () => {
    setShowCallMeForm(true)
  }

  const handleCallMeClose = () => {
    setShowCallMeForm(false)
  }

  // Computed value - should show video player
  const shouldShowVideo = !showContactForm && !showAccounts && selectedLanguage &&
                          !showQA && !showAccountsIntro && !showXroad

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100svh',
      paddingBottom: 'env(safe-area-inset-bottom)',
      overflow: 'hidden'
    }}>
      <div style={{
        width: '100%',
        height: `calc(100svh - ${CONTROL_BAR_HEIGHT}px)`,
        position: 'relative',
        overflow: 'hidden auto',
        transition: `opacity ${FADE_DURATION}ms ease`
      }}>
        {/* Page Manager - handles all full-page overlays */}
        <PageManager
          selectedLanguage={selectedLanguage}
          showXroad={showXroad}
          showAccounts={showAccounts}
          showAccountsIntro={showAccountsIntro}
          showContactForm={showContactForm}
          showQA={showQA}
          contractType={contractType}
          selectedAvatar={selectedAvatar}
          sourcePath={sourcePath}
          onSelectLanguage={onSelectLanguage}
          onSelectPage={onSelectPage}
          onPlanSelect={handlePlanSelect}
          onPlanIntroSelect={handlePlanIntroSelect}
          onShowFAQ={showQAPage}
          onFormSubmit={afterFormSend}
          onQABack={closeQAPage}
        />

        {/* Video Player - shown when not on a special page */}
        {shouldShowVideo && (
          <VideoPlayer
            videoRef={videoRef}
            sourceRef={sourceRef}
            currentIndex={currentIndex}
            showHover={showHover}
            showLinkOverlay={showLinkOverlay}
            linkUrl={linkUrl}
            showPanel={showPanel}
            showPanelButton={showPanelButton}
            panelData={panelData}
            onEnded={handleEnded}
            onLoadedData={handleLoadedData}
            onAvatarSelect={handleAvatarSelect}
            onPanelToggle={handlePanelToggle}
            onPanelClose={handlePanelClose}
          />
        )}
      </div>

      {/* Control Bar - hidden on Xroad page */}
      {!showXroad && (
        <ControlBar
          navigation={{
            hasPrev,
            hasNext,
            hasPrevCategory,
            hasNextCategory,
          }}
          titles={{
            prevTitle,
            nextTitle,
            prevCategoryTitle,
            nextCategoryTitle,
          }}
          isPaused={isPaused}
          selectedLanguage={selectedLanguage}
          selectedAvatar={selectedAvatar}
          currentIndex={currentIndex}
          onPrev={goToPrev}
          onNext={goToNext}
          onPrevCategory={goToPrevCategory}
          onNextCategory={goToNextCategory}
          onTogglePause={togglePause}
          onCallMe={handleCallMeClick}
        />
      )}

      {/* Call Me Form - overlay over everything */}
      {showCallMeForm && (
        <CallMeForm onClose={handleCallMeClose} sourcePath={sourcePath} />
      )}
    </div>
  )
}
